<script setup lang="ts">
import type { Partner, PartnerStore } from '@/types/types'
import { Lightbulb, Trash2, RefreshCw, Plus } from 'lucide-vue-next'

import CopyLink from '@/components/CopyLink.vue'
const props = defineProps<{
  account: any
  modelValue: any
  partner: Partner
  partnerStores: PartnerStore[]
  isLoading: boolean
}>()

const logoUrl = `${import.meta.env.VITE_AWS_STORAGE_URL}/logo-violet.png`
const { stores } = useLocalStorage()
const { openInNewTab } = useDOM()
const { howToUrl } = useIntegration()
const { t } = useI18n()
const emit = defineEmits<{
  (e: 'onRefresh'): void
  (e: 'update:modelValue', data: any)
}>()
const notification = useNotification()

const storeAssociations = ref([])
storeAssociations.value = props.modelValue
watch(
  () => props.modelValue,
  () => {
    storeAssociations.value = props.modelValue
  },
  { deep: true }
)

const updatePartnerStore = (index, newStoreId) => {
  if (index !== -1) {
    let newSelection = stores.value.find((store) => store.id === newStoreId)
    if (!newSelection?.text) {
      const selectPartnerStore = props.partnerStores.find((store) => store.storeId === newStoreId)
      if (selectPartnerStore) {
        newSelection.id = selectPartnerStore.storeId
        newSelection.name = selectPartnerStore.storeName
      }
    }

    if (newSelection) {
      storeAssociations.value[index] = {
        ...storeAssociations.value[index],
        hasError: false,
        dropdownSelection: {
          id: newSelection.id,
          text: newSelection.name
        }
      }
    } else {
      storeAssociations.value[index] = {
        ...storeAssociations.value[index],
        hasError: false,
        dropdownSelection: {
          id: null,
          text: t('pages.loyalty.create.instructions.noStore')
        }
      }
    }
    emit('update:modelValue', storeAssociations.value)
  }
}

/*
 * We provide an id of 0 and not null, to avoid js errors.
 * noStore is treated as a null id in the backend
 */
const storesValues = computed(() => {
  const allStores = stores.value.map((s) => ({
    id: s.id,
    text: s.name
  }))
  return [
    {
      id: 0,
      text: t('pages.loyalty.create.instructions.noStore')
    },
    ...allStores
  ]
})

const handleRefresh = () => emit('onRefresh')

const addStore = () => {
  if (storeAssociations.value.length >= stores.value.length) {
    notification.create('error', {
      text: t('pages.marketplace.marketplacePage.associationMax')
    })
    return
  }
  storeAssociations.value.push({
    isManual: true,
    storeIdFromPartner: '',
    storeNameFromPartner: '',
    partnerStoreId: '',
    hasError: false,
    hash: null,
    dropdownSelection: {
      id: null,
      text: t('pages.loyalty.create.instructions.noStore')
    }
  })
}

const deleteAssociation = (index) => {
  storeAssociations.value.splice(index, 1)
  emit('update:modelValue', storeAssociations.value)
}
</script>

<template>
  <div v-if="!isLoading" class="flex w-full flex-col">
    <BaseTip v-if="partner.usePartnerStoreHash" severity="info" class="mb-4">
      <template #icon>
        <Lightbulb />
      </template>
      <template #text>
        <p>
          {{ t('pages.loyalty.create.instructions.hash', { name: partner.name }) }}
        </p>
        <a
          class="flex items-center text-sm font-semibold text-primary hover:text-primary/80"
          href="#"
          @click="() => openInNewTab(partner.howToUrl || howToUrl)"
        >
          {{ t('pages.marketplace.marketplacePage.buttons.howToUrl') }}
          <BaseIcon name="HeroArrowTopRightOnSquareIconOutline" class="ml-1 h-4 w-4" />
        </a>
      </template>
    </BaseTip>

    <BaseTip v-if="storeAssociations.find((s) => s.hasError)" severity="error">
      <template #text>
        <p>
          {{ t('pages.loyalty.create.instructions.attention') }}
        </p>
        <p>
          {{ t('pages.loyalty.create.instructions.alreadySelected') }}
        </p>
      </template>
    </BaseTip>

    <table v-if="partner.usePartnerStoreHash" class="w-full text-left">
      <tbody>
        <tr>
          <th class="w-2/5 py-2">
            <div class="flex items-center gap-2">
              <img class="aspect-square w-6 rounded-xs border bg-white object-contain p-1" :src="partner.logo" />
              <h3 class="text-left font-semibold">
                {{ t('pages.loyalty.create.instructions.identificationName', { name: partner.name }) }}
              </h3>
            </div>
          </th>
          <th class="w-1/5"></th>
          <th class="w-2/5 py-2">
            <div class="flex items-center gap-2">
              <img class="aspect-square w-6 rounded-xs border bg-white object-contain p-0.5" :src="logoUrl" />
              <h3 class="text-left font-semibold">{{ t('pages.loyalty.create.instructions.pongoStore') }}</h3>
            </div>
          </th>
        </tr>
        <tr v-if="!storeAssociations.length" class="mx-auto max-w-lg p-6 text-center text-sm">
          <td colspan="4" class="py-10">
            <BaseIcon name="HeroArrowPathIconOutline" class="mx-auto mb-2 w-6" />
            <h3 class="mb-2 font-bold">{{ t('pages.loyalty.create.emptyStores.title') }}</h3>
            <p class="mb-2 text-foreground-secondary">
              {{ t('pages.loyalty.create.emptyDevices.associatePartner') }}
            </p>
          </td>
        </tr>
        <tr v-for="(store, index) in storeAssociations" :key="index" class="rounded-md bg-white p-3">
          <td :class="`relative w-2/5 py-2 pl-2 `">
            <CopyLink :link="store.hash" text="global.words.copy" class="w-full" />
          </td>
          <td class="relative w-1/5">
            <div :class="`absolute top-1/2 z-0 w-full border-t border-dashed border-border`" />
            <div
              :class="`absolute left-1/2 top-1/2 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full border bg-white p-1`"
            >
              <BaseIcon name="HeroArrowsRightLeftIconOutline" class="h-5 w-5 text-foreground-disabled" />
            </div>
          </td>
          <td class="w-1/5 py-2">
            <AppFormInput
              :id="store.dropdownSelection.text"
              :placeholder="store.dropdownSelection.text"
              readonly
              disabled
              type="text"
              name="name"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <table v-else class="w-full text-left">
      <tbody>
        <tr>
          <th class="w-2/6 py-2">
            <div class="flex items-center gap-2">
              <img class="aspect-square w-6 rounded-xs border bg-white object-contain p-1" :src="partner.logo" />
              <h3 class="text-left font-semibold">
                {{ t('pages.loyalty.create.instructions.storeName', { name: partner.name }) }}
              </h3>
            </div>
          </th>
          <th class="w-1/6"></th>
          <th class="w-2/6 py-2">
            <div class="flex items-center gap-2">
              <img class="aspect-square w-6 rounded-xs border bg-white object-contain p-0.5" :src="logoUrl" />
              <h3 class="text-left font-semibold">{{ t('pages.loyalty.create.instructions.pongoStore') }}</h3>
            </div>
          </th>
          <th class="w-1/6"></th>
        </tr>
        <tr
          v-if="!storeAssociations.length && !partner.usePartnerStoreHash"
          class="mx-auto max-w-lg p-6 text-center text-sm"
        >
          <td colspan="4" class="py-10">
            <BaseIcon name="HeroArrowPathIconOutline" class="mx-auto mb-2 w-6" />
            <h3 class="mb-2 font-bold">{{ t('pages.loyalty.create.emptyStores.title') }}</h3>
            <p class="mb-2 text-foreground-secondary">
              {{ t('pages.loyalty.create.emptyDevices.associatePartner') }}
            </p>
          </td>
        </tr>
        <tr
          v-if="!storeAssociations.length && partner.usePartnerStoreHash"
          class="mx-auto max-w-lg p-6 text-center text-sm"
        >
          <td colspan="4" class="py-10">
            <BaseIcon name="HeroArrowPathIconOutline" class="mx-auto mb-2 w-6" />
            <h3 class="mb-2 font-bold">{{ t('pages.loyalty.create.emptyStores.title') }}</h3>
            <p class="mb-2 text-foreground-secondary">
              {{ t('pages.loyalty.create.emptyDevices.associatePartnerHash') }}
            </p>
          </td>
        </tr>
        <tr
          v-for="(store, index) in storeAssociations"
          :key="index"
          :class="{ 'rounded-md bg-white p-3': !store.hasError, 'rounded-md bg-red-100 p-3': store.hasError }"
        >
          <td :class="`relative w-2/6 py-2 ${store.hasError ? 'pl-4' : 'pl-2'} `">
            <div v-if="store.hasError" class="absolute inset-y-0 left-0 w-1 bg-red-400"></div>
            <div v-if="!store.isManual" className="bg-muted p-2 rounded-md border text-foreground">
              {{
                !store.storeIdFromPartner && !store.storeNameFromPartner
                  ? t('pages.loyalty.create.emptyDevices.notAvailable', { partnerName: partner.name })
                  : ''
              }}
              {{ store.storeIdFromPartner }}
              {{ store.storeNameFromPartner !== null ? store.storeNameFromPartner : '' }}
            </div>
            <AppFormInput
              v-else
              :id="store.storeIdFromPartner"
              v-model="store.storeIdFromPartner"
              type="text"
              placeholder="Saisie manuelle"
              name="name"
            />
          </td>
          <td class="relative w-1/6">
            <div
              :class="`absolute top-1/2 z-0 w-full border-t border-dashed ${
                store.hasError ? 'border-red-300' : 'border-border'
              }`"
            />
            <div
              :class="`absolute left-1/2 top-1/2 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full border ${
                store.hasError ? 'border-red-200 bg-red-200' : 'border-border bg-white'
              } p-1`"
            >
              <ArrowsRightLeftIcon
                :class="{ 'h-5 w-5 text-foreground-disabled': !store.hasError, 'h-5 w-5 text-red-500': store.hasError }"
              />
            </div>
          </td>
          <td class="w-2/6 py-2">
            <ComboboxSelect
              v-model="store.dropdownSelection"
              value-key="id"
              label-key="text"
              :model-value="store.dropdownSelection"
              trigger-class="w-full"
              :placeholder="store.dropdownSelection.text"
              :options="storesValues"
              @update:model-value="updatePartnerStore(index, $event)"
            />
          </td>
          <td class="relative w-1/6 text-center">
            <Button variant="ghost" size="icon" @click="deleteAssociation(index)">
              <Trash2 class="text-destructive" />
            </Button>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="mt-5 flex items-center justify-end gap-x-2">
      <Button variant="ghost-primary" @click="() => handleRefresh()">
        <RefreshCw />
        <span class="text-sm">{{ t('pages.loyalty.create.emptyDevices.button') }}</span>
      </Button>
      <Button v-if="!partner.usePartnerStoreHash" variant="ghost-primary" @click="() => addStore()">
        <Plus />
        <span class="text-sm">{{ t('pages.loyalty.create.emptyDevices.addButton') }}</span>
      </Button>
    </div>
  </div>
  <AppSkeleton v-else type="section" />
</template>
